import {ViewController} from "data/types/structure";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";
import {inject, injectable} from "inversify";
import {makeAutoObservable} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import {eq, findIndex, includes, orderBy, sum, take} from "lodash";

export interface IScoringKeysController extends ViewController {
	readonly i18n: ILocalizationStore;

	get correctPicks(): number;
	get notHitPicks(): number;
	get incorrectPicks(): number;
}

@injectable()
export class ScoringKeysController implements IScoringKeysController {
	get correctPicks() {
		const nfcStandings = this._standingsStore.standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, this._conferenceStore.nfcConferenceId);
			}
			return false;
		});

		const afcStandings = this._standingsStore.standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, this._conferenceStore.afcConferenceId);
			}
			return false;
		});

		const orderedNfcStandings = take(orderBy(nfcStandings, ["conferenceRank"], ["asc"]), 7);

		const orderedAfcStandings = take(orderBy(afcStandings, ["conferenceRank"], ["asc"]), 7);

		const nfcCorrectPicks = this._lpStore.nfcPredictions.filter((it) => {
			return (
				it.position ===
				findIndex(orderedNfcStandings, (el) => el.squadId === it.squadId) + 1
			);
		}).length;

		const afcCorrectPicks = this._lpStore.afcPredictions.filter((it) => {
			return (
				it.position ===
				findIndex(orderedAfcStandings, (el) => el.squadId === it.squadId) + 1
			);
		}).length;

		return sum([nfcCorrectPicks, afcCorrectPicks]);
	}

	get notHitPicks() {
		const nfcStandings = this._standingsStore.standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, this._conferenceStore.nfcConferenceId);
			}
			return false;
		});

		const afcStandings = this._standingsStore.standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, this._conferenceStore.afcConferenceId);
			}
			return false;
		});

		const orderedNfcStandings = take(orderBy(nfcStandings, ["conferenceRank"], ["asc"]), 7);

		const orderedAfcStandings = take(orderBy(afcStandings, ["conferenceRank"], ["asc"]), 7);

		const nfcCorrectPicks = this._lpStore.nfcPredictions.filter((it) => {
			return (
				it.position !==
					findIndex(orderedNfcStandings, (el) => el.squadId === it.squadId) + 1 &&
				includes(
					orderedNfcStandings.map((it) => it.squadId),
					it.squadId
				)
			);
		}).length;

		const afcCorrectPicks = this._lpStore.afcPredictions.filter((it) => {
			return (
				it.position !==
					findIndex(orderedAfcStandings, (el) => el.squadId === it.squadId) + 1 &&
				includes(
					orderedAfcStandings.map((it) => it.squadId),
					it.squadId
				)
			);
		}).length;

		return sum([nfcCorrectPicks, afcCorrectPicks]);
	}

	get incorrectPicks() {
		if (this._lpStore.hasLPPredictions) {
			return 14 - this.correctPicks - this.notHitPicks;
		}
		return 0;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore
	) {
		makeAutoObservable(this);
	}

	init(param: void) {
		return;
	}
}
